import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { useAuth } from "../../auth/AuthContext";
import LoadingPage from "../../Loader";
import { useNavigate } from "react-router-dom";
import ModuleSelection from "./ModuleSelection";
import { initiateDPOPayment, redirectToDPOPayment } from "./DPOPaymentService";
import primaryBackground from "../../assets/Backgrounds/primaryBg.svg";

const countryCodes = [
  { code: "+264", label: "Namibia (+264)" },
  { code: "+27", label: "South Africa (+27)" },
  { code: "+1", label: "United States (+1)" },
  { code: "+44", label: "United Kingdom (+44)" },
];

const AddStudent = () => {
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    idNumber: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    city: "",
    postalCode: "",
    phoneNumber: "",
    phoneCountryCode: countryCodes[0].code,
    school: "",
    grade: "",
    selectedModules: [],
    paymentMethod: "DPO",
    acceptTerms: false,
  });
  const [errors, setErrors] = useState({});
  const { getData, addData } = useAuth();
  const navigate = useNavigate();
  const authStatus = JSON.parse(localStorage.getItem("authStatus")) || {};
  const user = authStatus.user || {};

  const steps = ["Student Details", "Modules Selection", "Payment"];

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await getData("proccess.php", "modules_V2");
        setModules(
          response.success
            ? response.data.map((m) => ({
                id: m.id,
                name: m.module_name,
                price: parseFloat(m.price),
                description: m.description,
                short_description: m.short_description,
                age_target_group: m.age_target_group,
                duration: m.duration,
                total_hours: m.total_hours,
                study_mode: m.study_mode,
                delivery_time: m.delivery_time,
              }))
            : []
        );
      } catch (error) {
        console.error("Module fetch error:", error);
        alert(`Error fetching modules: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, [getData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let sanitizedValue = value;
    if (name.includes("phoneNumber")) {
      sanitizedValue = value.replace(/[^0-9]/g, "");
    } else if (name === "postalCode") {
      sanitizedValue = value.replace(/[^0-9]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : sanitizedValue,
    }));
  };

  const handleModuleSelection = (moduleId) => {
    setFormData((prev) => ({
      ...prev,
      selectedModules: prev.selectedModules.includes(moduleId)
        ? prev.selectedModules.filter((id) => id !== moduleId)
        : [...prev.selectedModules, moduleId],
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    const validators = {
      0: () => {
        if (!formData.firstName.match(/^[a-zA-Z]{2,}$/))
          newErrors.firstName = "Valid first name is required";
        if (!formData.lastName.match(/^[a-zA-Z]{2,}$/))
          newErrors.lastName = "Valid last name is required";
        if (!formData.phoneNumber.match(/^\d{7,}$/))
          newErrors.phoneNumber = "Valid phone number is required";
        if (!formData.idNumber.match(/^\d{6,}$/))
          newErrors.idNumber = "Valid ID number is required";
        if (!formData.dateOfBirth)
          newErrors.dateOfBirth = "Date of birth is required";
        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!formData.address.match(/^[a-zA-Z0-9\s]{5,}$/))
          newErrors.address = "Valid address is required";
        if (!formData.city.match(/^[a-zA-Z\s]{2,}$/))
          newErrors.city = "Valid city is required";
        if (!formData.postalCode.match(/^\d{4,5}$/))
          newErrors.postalCode = "Valid postal code is required";
        if (!formData.school.match(/^[a-zA-Z\s]{2,}$/))
          newErrors.school = "School is required";
        if (!formData.grade.match(/^[a-zA-Z0-9\s]{1,}$/))
          newErrors.grade = "Grade is required";
      },
      1: () => {
        if (formData.selectedModules.length === 0)
          newErrors.selectedModules = "At least one module must be selected";
      },
      2: () => {
        if (!formData.acceptTerms)
          newErrors.acceptTerms = "You must accept the terms and conditions";
      },
    };

    if (validators[step]) validators[step]();
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const generateToken = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };

  const calculateTotal = () => {
    return formData.selectedModules.reduce((total, moduleId) => {
      const module = modules.find((m) => m.id === moduleId);
      return total + (module?.price || 0);
    }, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    try {
      setLoading(true);

      const course = formData.selectedModules
        .map((id) => modules.find((m) => m.id === id)?.name)
        .filter(Boolean)
        .join(", ");
      const physicalAddress = [
        formData.address,
        formData.city,
        formData.postalCode,
      ]
        .filter(Boolean)
        .join(", ");
      const age = formData.dateOfBirth
        ? Math.floor(
            (new Date() - new Date(formData.dateOfBirth)) / 31557600000
          )
        : null;

      // Fetch parent data to get parent_id
      const parentResponse = await getData("proccess.php", "parent_V2");
      if (!parentResponse.success || !Array.isArray(parentResponse.data)) {
        throw new Error("Failed to fetch parent data");
      }
      const parent = parentResponse.data.find((p) => p.email === user.email);
      if (!parent) {
        throw new Error("Parent data not found");
      }

      // Add student
      const studentData = {
        registration_type: "parent",
        parent_id: parent.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: generateToken(), // Generate a temporary password for the student
        token: generateToken(),
        age: age,
        school: formData.school,
        grade: formData.grade,
        physicalAddress: physicalAddress,
        course: course,
        status: 0,
        trans_token: "",
        trans_status: "",
        trans_result_explanation: "",
        dueToUs: calculateTotal(),
        dueToYou: 0,
        email: `${formData.firstName.toLowerCase()}.${formData.lastName.toLowerCase()}@student.example.com`, // Placeholder email
        cell: `${formData.phoneCountryCode}${formData.phoneNumber}`,
      };

      const studentResponse = await addData("proccess.php", "students_V2", studentData);
      if (!studentResponse.success) {
        throw new Error(studentResponse.message || "Student registration failed");
      }

      const paymentData = {
        amount: calculateTotal(),
        currency: "NAD",
        customerEmail: user.email,
        customerFirstName: parent.fullName.split(" ")[0],
        customerLastName: parent.fullName.split(" ").slice(1).join(" "),
        customerPhone: parent.cell,
        selectedModules: formData.selectedModules
          .map((id) => modules.find((m) => m.id === id)?.name)
          .filter(Boolean),
        serviceType: "3854",
        serviceDescription: "Student Registration",
        redirectURL: `${window.location.origin}/payment/success`,
        backURL: `${window.location.origin}/payment/cancel`,
      };

      const result = await initiateDPOPayment(paymentData);
      if (result.success) {
        redirectToDPOPayment(result.paymentUrl);
      } else {
        alert(`Payment error: ${result.message}`);
      }
    } catch (error) {
      console.error("Registration error:", error);
      alert(`Registration error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingPage />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `url(${primaryBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        p: 3,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1200,
          marginTop: "45px",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 3,
          p: 4,
          animation:
            Object.keys(errors).length > 0
              ? "flashError 0.9s ease-in-out 3"
              : "none",
          "@keyframes flashError": {
            "0%, 33%, 66%, 100%": { backgroundColor: "background.paper" },
            "16.5%, 49.5%": { backgroundColor: "#f44336" },
          },
        }}
      >
        <Typography variant="h4" align="center" gutterBottom sx={{ mb: 4 }}>
          Add New Student
        </Typography>

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box sx={{ width: "100%", maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              Student Details
            </Typography>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              margin="normal"
              error={!!errors.firstName}
              helperText={errors.firstName}
              inputProps={{ pattern: "[a-zA-Z]{2,}" }}
            />
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              margin="normal"
              error={!!errors.lastName}
              helperText={errors.lastName}
              inputProps={{ pattern: "[a-zA-Z]{2,}" }}
            />
            <TextField
              fullWidth
              label="ID Number"
              name="idNumber"
              value={formData.idNumber}
              onChange={handleChange}
              margin="normal"
              error={!!errors.idNumber}
              helperText={errors.idNumber}
              inputProps={{ maxLength: 20 }}
            />
            <TextField
              fullWidth
              label="Date of Birth"
              name="dateOfBirth"
              type="date"
              value={formData.dateOfBirth}
              onChange={handleChange}
              margin="normal"
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth margin="normal" error={!!errors.gender}>
              <InputLabel>Gender</InputLabel>
              <Select
                name="gender"
                value={formData.gender}
                label="Gender"
                onChange={handleChange}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
              {errors.gender && (
                <Typography color="error" variant="caption">
                  {errors.gender}
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              margin="normal"
              error={!!errors.address}
              helperText={errors.address}
            />
            <TextField
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              margin="normal"
              error={!!errors.city}
              helperText={errors.city}
            />
            <TextField
              fullWidth
              label="Postal Code"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              margin="normal"
              error={!!errors.postalCode}
              helperText={errors.postalCode}
              inputProps={{ pattern: "[0-9]*", maxLength: 5 }}
            />
            <TextField
              fullWidth
              label="School"
              name="school"
              value={formData.school}
              onChange={handleChange}
              margin="normal"
              error={!!errors.school}
              helperText={errors.school}
            />
            <TextField
              fullWidth
              label="Grade"
              name="grade"
              value={formData.grade}
              onChange={handleChange}
              margin="normal"
              error={!!errors.grade}
              helperText={errors.grade}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Country Code</InputLabel>
                <Select
                  name="phoneCountryCode"
                  value={formData.phoneCountryCode}
                  label="Country Code"
                  onChange={handleChange}
                >
                  {countryCodes.map(({ code, label }) => (
                    <MenuItem key={code} value={code}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
                margin="normal"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                inputProps={{ pattern: "[0-9]{7,}", maxLength: 15 }}
              />
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <ModuleSelection
            modules={modules}
            selectedModules={formData.selectedModules}
            onModuleSelection={handleModuleSelection}
            errors={errors}
            calculateTotal={calculateTotal}
          />
        )}

        {activeStep === 2 && (
          <Box sx={{ width: "100%", maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Payment Information
            </Typography>

            <Box
              sx={{
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                mb: 3,
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Payment Summary
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography>Selected Modules:</Typography>
                <Typography>{formData.selectedModules.length}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography>Total Amount:</Typography>
                <Typography>N${calculateTotal().toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}
              >
                <Typography>Amount Due:</Typography>
                <Typography>N${calculateTotal().toFixed(2)}</Typography>
              </Box>
            </Box>

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Typography component="legend">Payment Method</Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <img
                  src="https://s3-us-west-2.amazonaws.com/cbi-image-service-prd/modified/09836cd2-75e1-4a15-8160-e98a4b12c9b2.png"
                  alt="DPO"
                  style={{ height: 20, marginLeft: 8 }}
                />
              </Box>
            </FormControl>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label="I agree to the terms and conditions"
              />
              {errors.acceptTerms && (
                <Typography color="error" variant="caption">
                  {errors.acceptTerms}
                </Typography>
              )}
            </Box>

            <Typography variant="body2" sx={{ mt: 2 }}>
              By clicking "Complete Registration", you will be redirected to DPO's secure payment page to complete your payment.
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">
            Back
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!formData.acceptTerms}
            >
              Complete Registration
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AddStudent;